import React, { useState, useEffect, useCallback } from "react";
import Select from "Components/Common/Select/Select";
import "Pages/GcPrecon/ChangeOrder/styles/changeOrder.scss";
import { get } from "../../../../utils/api/apiCaller";
import { ENDPOINTS } from "../../../../utils/api/endpoints";
import { exportChangeOrdersCSV } from "../../../../services/adminService";
import { useToast } from "Components/Common/Toast/ToastContext";
import useScrollToTop from "hooks/useScrollToTop";
import CoLogTable from "../../../GcPrecon/ChangeOrder/components/CoLogTable";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import {
  LogRow,
  TimelineItem,
} from "../../../GcPrecon/ChangeOrder/constants/changeOrderData";
import Button from "Components/Common/Button/Button";
import Table, { type Column } from "Components/Table/Table";
import EmptyState from "Components/Common/EmptyState";
import EmptyChangeOrderIcon from "Assets/Img/EmptyChangeOrderIcon";
import ChangeOrderDetailsView, {
  type CODetailData,
} from "../../../Bank/BankDetails/ChangeOrderDetailsView";
import Pagination from "Components/Common/Pagination/Pagination";
import {
  CO_STATUS,
  CO_STATUS_LABELS,
  normalizeChangeOrderStatus,
} from "../../../GcPrecon/ChangeOrder/constants/changeOrderStatus";

// ── API types ─────────────────────────────────────────────────────────────────

interface RawCOAmount {
  amount?: number;
  currency?: { symbol?: string; code?: string };
}

interface RawCODocument {
  id?: string;
  title?: string;
  name?: string;
  fileName?: string;
  key?: string;
  document?: { fileName?: string; url?: string };
}

interface RawLienItem {
  id?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  unitCost?: RawCOAmount;
  amount?: RawCOAmount;
  displayOrder?: number;
  aiaItem?: {
    id?: string;
    itemCode?: number;
    itemName?: string;
    itemLabel?: string;
  };
  subContractor?: { id?: string; companyName?: string } | null;
}

interface RawCOActivity {
  title: string;
  description?: string;
  meta?: { companyName?: string; date?: string; days?: number };
}

interface RawCO {
  id: string;
  number?: string;
  coNumber?: string;
  title?: string;
  description?: string;
  date?: string;
  cause?: string;
  tradeDivisions?: string;
  status?: string;
  amount?: RawCOAmount;
  changeAmount?: RawCOAmount;
  totalAmount?: RawCOAmount;
  extendedDays?: number;
  scheduleImpact?: number | string;
  scheduleImpactDays?: number;
  projectName?: string;
  publishedByCompany?: string;
  publishedAt?: string;
  submittedAt?: string;
  createdAt?: string;
  approvedAt?: string;
  lenderApprovedAt?: string;
  lenderRejectedAt?: string;
  gc?: { companyName?: string };
  contractor?: { companyName?: string };
  project?: { name?: string };
  lender?: { bankName?: string; companyName?: string };
  gcName?: string;
  bankName?: string;
  documents?: RawCODocument[];
  lienItems?: RawLienItem[];
  activities?: RawCOActivity[];
  notes?: string;
}

interface COListResponse {
  data?: RawCO[];
  pagination?: {
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  };
  addOns?: {
    originalContractValue?: RawCOAmount;
    approvedCoSum?: { amount?: RawCOAmount; count?: number };
    pendingCoSum?: { amount?: RawCOAmount; count?: number };
    revisedContractValue?: RawCOAmount;
    revisedContractValueWithPendingCO?: RawCOAmount;
    totalCoCount?: number;
  };
}

// ── Status constants ───────────────────────────────────────────────────────────

const S_PENDING = CO_STATUS.PENDING;
const S_APPROVED = CO_STATUS.APPROVED;
const S_REJECTED = CO_STATUS.REJECTED;

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: S_PENDING, label: "Pending" },
  { value: S_APPROVED, label: "Approved" },
  { value: S_REJECTED, label: "Rejected" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

// The backend renamed the "approved" status value when Change Order approval
// moved from Lender to Owner. Normalize any change order still carrying the
// old value so every status comparison below only has to know one value.
function normalizeCoStatus(co: RawCO): RawCO {
  const status = normalizeChangeOrderStatus(co.status);
  return status === co.status ? co : { ...co, status };
}

function getSymbol(raw?: RawCOAmount): string {
  return raw?.currency?.symbol ?? "$";
}

function formatMoney(raw?: RawCOAmount): string {
  if (!raw || raw.amount == null) return "—";
  const sym = getSymbol(raw);
  return `${sym}${Math.abs(raw.amount).toLocaleString("en-US")}`;
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatScheduleImpact(val?: number | string): string {
  if (val == null || val === "" || val === 0) return "None";
  if (typeof val === "string") return val;
  return `${val > 0 ? "+" : ""}${val} day${Math.abs(val) !== 1 ? "s" : ""}`;
}

function getAmount(co: RawCO): RawCOAmount | undefined {
  return co.totalAmount ?? co.amount ?? co.changeAmount;
}

function buildTimeline(co: RawCO): TimelineItem[] {
  if (co.activities && co.activities.length > 0) {
    return co.activities.map((act, i) => ({
      id: i + 1,
      status:
        act.title.toLowerCase().includes("awaiting bank approval") ||
        act.title.toLowerCase().includes("awaiting owner approval")
          ? "pending"
          : "done",
      title: act.title,
      meta:
        [act.meta?.companyName, formatDate(act.meta?.date)]
          .filter(Boolean)
          .join(" — ") ||
        act.description ||
        "",
    }));
  }
  const isPending = co.status === S_PENDING;
  const gcName =
    co.publishedByCompany ??
    co.gc?.companyName ??
    co.contractor?.companyName ??
    "GC";
  const submittedDate = co.publishedAt ?? co.submittedAt ?? co.createdAt;
  return [
    {
      id: 1,
      status: "done",
      title: "Submitted by GC",
      meta: `${gcName} — ${formatDate(submittedDate)}`,
    },
    {
      id: 2,
      status: "done",
      title: "CSC New England notified",
      meta: "CSC notified upon submission",
    },
    {
      id: 3,
      status: isPending ? "pending" : "done",
      title: isPending ? "Awaiting Owner Approval" : "Decision recorded",
      meta: isPending
        ? "Pending owner review"
        : (co.lenderApprovedAt ?? co.approvedAt)
          ? `Resolved ${formatDate(co.lenderApprovedAt ?? co.approvedAt)}`
          : "Resolved",
    },
  ];
}

function mapToLogRow(co: RawCO): LogRow {
  const amt = getAmount(co);
  const sym = getSymbol(amt);
  const num = amt?.amount;
  const amountStr =
    num != null ? `+${sym}${Math.abs(num).toLocaleString("en-US")}` : "—";
  const s = CO_STATUS_LABELS[co.status ?? ""] ?? {
    label: co.status ?? "—",
    cls: "bPending",
  };
  const submittedDate = co.publishedAt ?? co.submittedAt ?? co.createdAt;
  return {
    id: co.number ?? co.coNumber ?? co.id,
    dbId: co.id,
    description: co.title ?? "—",
    sub: formatDate(submittedDate),
    submitted: formatDate(submittedDate),
    amount: amountStr,
    amountTone: "pos",
    daysImpact: formatScheduleImpact(
      co.extendedDays ?? co.scheduleImpact ?? co.scheduleImpactDays,
    ),
    status: s.label,
    statusClass: s.cls,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminChangeOrders({
  projectId,
}: {
  projectId: string;
}) {
  const showToast = useToast();

  const [pendingCOs, setPendingCOs] = useState<RawCO[]>([]);
  const [approvedCOs, setApprovedCOs] = useState<RawCO[]>([]);
  const [activeCO, setActiveCO] = useState<RawCO | null>(null);
  const [activeCODetail, setActiveCODetail] = useState<RawCO | null>(null);
  const [viewedApprovedCO, setViewedApprovedCO] = useState<RawCO | null>(null);
  const [viewedApprovedCODetail, setViewedApprovedCODetail] =
    useState<RawCO | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewedDetailLoading, setViewedDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [exporting, setExporting] = useState(false);

  useScrollToTop(viewedApprovedCO?.id ?? "");

  const fetchCOs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");

    const { data, error: apiError } = (await get(
      ENDPOINTS.ADMIN.CHANGE_ORDERS(projectId, filterStatus, page, limit),
    )) as { data: COListResponse | null; error: { message?: string } | null };

    setLoading(false);

    if (apiError) {
      setError(apiError.message ?? "Failed to load change orders.");
      return;
    }

    setPagination({
      total: data?.pagination?.total ?? 0,
      totalPages: data?.pagination?.totalPages ?? 0,
    });

    const all = (data?.data ?? []).map(normalizeCoStatus);
    const pending = all.filter((co) => co.status === S_PENDING);
    const nextActiveCO = pending[0] ?? null;

    setPendingCOs(pending);
    setApprovedCOs(all.filter((co) => co.status !== S_PENDING));
    setActiveCO(nextActiveCO);
    if (!nextActiveCO) setActiveCODetail(null);
  }, [projectId, filterStatus, page, limit]);

  useEffect(() => {
    fetchCOs();
  }, [fetchCOs]);

  // Fetch detail for the active pending CO
  useEffect(() => {
    if (!activeCO?.id || !projectId) return;
    let cancelled = false;
    setDetailLoading(true);
    setActiveCODetail(null);

    (async () => {
      const { data, error: apiError } = (await get(
        ENDPOINTS.CHANGE_ORDERS.LENDER_DETAIL(projectId, activeCO.id),
      )) as {
        data: { data?: RawCO } | null;
        error: { message?: string } | null;
      };
      if (cancelled) return;
      setDetailLoading(false);
      if (!apiError && data?.data)
        setActiveCODetail(normalizeCoStatus(data.data));
    })();

    return () => {
      cancelled = true;
    };
  }, [activeCO?.id, projectId]);
  // Fetch detail for a viewed approved CO
  useEffect(() => {
    if (!viewedApprovedCO?.id || !projectId) return;
    let cancelled = false;
    setViewedDetailLoading(true);
    setViewedApprovedCODetail(null);

    (async () => {
      const { data, error: apiError } = (await get(
        ENDPOINTS.CHANGE_ORDERS.LENDER_DETAIL(projectId, viewedApprovedCO.id),
      )) as {
        data: { data?: RawCO } | null;
        error: { message?: string } | null;
      };
      if (cancelled) return;
      setViewedDetailLoading(false);
      if (!apiError && data?.data)
        setViewedApprovedCODetail(normalizeCoStatus(data.data));
    })();

    return () => {
      cancelled = true;
    };
  }, [viewedApprovedCO?.id, projectId]);

  const handleExportCSV = async () => {
    setExporting(true);
    const result = await exportChangeOrdersCSV(
      projectId,
      filterStatus || undefined,
    );
    if (result.success) {
      showToast("✓ " + result.message);
    } else {
      showToast("✗ " + result.message);
    }
    setExporting(false);
  };

  const co = activeCODetail ?? activeCO;
  const timeline = co ? buildTimeline(co) : [];
  const amt = co ? getAmount(co) : undefined;
  const amountDisplay = amt ? `+${formatMoney(amt)}` : "—";
  const submittedOn = co?.publishedAt ?? co?.submittedAt;
  const daysAwaiting = submittedOn
    ? Math.floor((Date.now() - new Date(submittedOn).getTime()) / 86_400_000)
    : null;

  return (
    <section className="changeOrderPage">
      <h1 className="sectionTitle">Change Order Log</h1>
      <p className="sectionSub">
        Full change order history for this project. CSC is notified of all
        submissions and decisions for record-keeping purposes.
      </p>

      {/* <SummaryStrip items={summaryItems} /> */}

      {viewedApprovedCO ? (
        <ChangeOrderDetailsView
          co={(viewedApprovedCODetail ?? viewedApprovedCO) as CODetailData}
          loading={viewedDetailLoading}
          onClose={() => {
            setViewedApprovedCO(null);
            setViewedApprovedCODetail(null);
          }}
        />
      ) : (
        <>
          <div className="card">
            <div className="cardTitle withActions">
              ALL CHANGE ORDERS
              <div className="actionsInline">
                <Select
                  className="mini"
                  options={STATUS_FILTER_OPTIONS}
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(1);
                    setActiveCO(null);
                    setActiveCODetail(null);
                  }}
                />

                <Button
                  className="btn btnSecondary"
                  onClick={handleExportCSV}
                  disabled={exporting}
                >
                  {exporting ? (
                    <>
                      <span className="btn-spinner" /> Exporting
                    </>
                  ) : (
                    "Export CSV"
                  )}
                </Button>
              </div>
            </div>

            {loading ? (
              <TableSkeleton cols={7} rows={5} />
            ) : error ? (
              <div className="errorState">{error}</div>
            ) : approvedCOs.length > 0 ? (
              <>
                <CoLogTable
                  rows={approvedCOs.map((co) => {
                    const row = mapToLogRow(co);
                    const gcName =
                      co.gcName ??
                      co.gc?.companyName ??
                      co.contractor?.companyName ??
                      "";
                    const bankName =
                      co.bankName ??
                      co.lender?.bankName ??
                      co.lender?.companyName ??
                      "";
                    return {
                      ...row,
                      sub:
                        [gcName, bankName].filter(Boolean).join(" - ") ||
                        row.sub,
                    };
                  })}
                  onOpenRow={(row) => {
                    const raw = approvedCOs.find((c) => c.id === row.dbId);
                    if (raw) {
                      setViewedApprovedCO(raw);
                      setViewedApprovedCODetail(null);
                    }
                  }}
                />
                {pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={page}
                    totalPages={pagination.totalPages}
                    total={pagination.total}
                    limit={limit}
                    onPageChange={setPage}
                  />
                )}
              </>
            ) : (
              <EmptyState
                noBorder={true}
                height="100px"
                icon={<EmptyChangeOrderIcon />}
                title="No Change Orders"
                subtitle="There are no change orders for this project yet."
              />
            )}
          </div>

          {!loading && !error && (
            <>
              {pendingCOs.length > 1 && (
                <div className="actionsInline" style={{ marginBottom: 12 }}>
                  {pendingCOs.map((pco) => (
                    <Button
                      key={pco.id}
                      className={`btn btnSm ${activeCO?.id === pco.id ? "btnPrimary" : "btnSecondary"}`}
                      onClick={() => {
                        setActiveCO(pco);
                        setActiveCODetail(null);
                      }}
                    >
                      {pco.number ?? pco.coNumber ?? pco.id}
                    </Button>
                  ))}
                </div>
              )}

              {co ? (
                <article className="coDetailCard">
                  <div className="coDetailHeader">
                    <div>
                      <p className="coLabel">
                        {co.status === S_APPROVED
                          ? "Change Order - Approved"
                          : co.status === S_REJECTED
                            ? "Change Order - Rejected"
                            : "Change Order - Pending Owner Approval"}
                      </p>
                      <h2>
                        {co.number ?? "—"} — {co.title ?? "—"}
                      </h2>
                      <p className="coMeta">
                        {[
                          co.projectName ?? co.project?.name,
                          (co.publishedByCompany ??
                          co.gc?.companyName ??
                          co.contractor?.companyName)
                            ? `Submitted by ${co.publishedByCompany ?? co.gc?.companyName ?? co.contractor?.companyName}`
                            : null,
                          formatDate(co.publishedAt ?? co.submittedAt),
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </p>
                    </div>
                    <div className="coAmountBox">
                      <span>Change Amount</span>
                      <strong>{amountDisplay}</strong>
                      {co.status === S_PENDING ? (
                        <span className="badge bPending">
                          <span className="dot" />
                          Awaiting Owner Approval
                        </span>
                      ) : co.status === S_APPROVED ? (
                        <span className="badge bApproved">
                          <span className="dot" />
                          Owner Approved
                        </span>
                      ) : co.status === S_REJECTED ? (
                        <span className="badge bRejected">
                          <span className="dot" />
                          Rejected
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="impactBar">
                    <div>
                      <strong className="up">{amountDisplay}</strong>
                      <span>Contract Increase</span>
                    </div>
                    <div>
                      <strong className="amber">
                        {formatScheduleImpact(
                          co.extendedDays ??
                            co.scheduleImpact ??
                            co.scheduleImpactDays,
                        )}
                      </strong>
                      <span>Schedule Impact</span>
                    </div>
                    <div>
                      <strong>{co.documents?.length ?? 0}</strong>
                      <span>Documents Attached</span>
                    </div>
                    <div>
                      <strong>
                        {daysAwaiting != null
                          ? `${daysAwaiting} day${daysAwaiting !== 1 ? "s" : ""}`
                          : "—"}
                      </strong>
                      <span>Days Since Submitted</span>
                    </div>
                  </div>

                  <div className="coBody">
                    {detailLoading ? (
                      <TableSkeleton cols={1} rows={4} />
                    ) : (
                      <>
                        <h3>Description of Change</h3>
                        <p>{co.description ?? "No description provided."}</p>

                        {(co.cause || co.tradeDivisions) && (
                          <div className="coMetaGrid">
                            {co.cause && (
                              <div>
                                <h3>Reason / Cause</h3>
                                <p>{co.cause}</p>
                              </div>
                            )}
                            {co.tradeDivisions && (
                              <div>
                                <h3>Trade Division</h3>
                                <p>{co.tradeDivisions}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {co.lienItems &&
                          co.lienItems.length > 0 &&
                          (() => {
                            const totalAmt = co.lienItems!.reduce(
                              (sum, item) => sum + (item.amount?.amount ?? 0),
                              0,
                            );
                            const columns: Column<RawLienItem>[] = [
                              {
                                header: (
                                  <>
                                    A
                                    <span className="tableSubTitle">
                                      Item #
                                    </span>
                                  </>
                                ),
                                className: "center",
                                render: (item) => item.displayOrder ?? "—",
                              },
                              {
                                header: "CSI Code",
                                render: (item) => item.aiaItem?.itemCode ?? "—",
                              },
                              {
                                header: (
                                  <>
                                    B
                                    <span className="tableSubTitle">
                                      Description of Work
                                    </span>
                                  </>
                                ),
                                render: (item) => item.description ?? "—",
                              },
                              {
                                header: (
                                  <>
                                    C
                                    <span className="tableSubTitle">
                                      Scheduled Value
                                    </span>
                                  </>
                                ),
                                className: "num",
                                render: (item) => {
                                  const a = item.amount?.amount ?? 0;
                                  const sym =
                                    item.amount?.currency?.symbol ?? "$";
                                  return `${sym}${a.toLocaleString("en-US")}`;
                                },
                              },
                              {
                                header: "% of CONTRACT",
                                className: "num",
                                render: (item) => {
                                  const a = item.amount?.amount ?? 0;
                                  return totalAmt > 0
                                    ? `${((a / totalAmt) * 100).toFixed(1)}%`
                                    : "0.0%";
                                },
                              },
                              {
                                header: "Subcontractor / Vendor",
                                render: (item) =>
                                  item.subContractor?.companyName ?? "—",
                              },
                            ];
                            const footer: React.ReactNode[] = [
                              <span key="label" className="coTableLabel">
                                Total Change Order Amount
                              </span>,
                              null,
                              null,
                              <span key="total" className="coTableAmount">
                                {formatMoney({
                                  amount: totalAmt,
                                  currency:
                                    co.lienItems![0]?.amount?.currency ??
                                    co.totalAmount?.currency,
                                })}
                              </span>,
                              <span key="pct" className="muted">
                                {totalAmt > 0 ? "100.0%" : "0.0%"}
                              </span>,
                              null,
                            ];
                            return (
                              <>
                                <h3>Budget Breakdown</h3>
                                <Table<RawLienItem>
                                  data={co.lienItems!}
                                  columns={columns}
                                  footerRow={footer}
                                  tableClassName="coTable"
                                  footerRowClassName="coTableFooter"
                                />
                              </>
                            );
                          })()}

                        <h3>Attached Documents</h3>
                        {co.documents && co.documents.length > 0 ? (
                          <div className="fileChipWrap">
                            {co.documents.map((doc, i) => {
                              const label =
                                doc.title ??
                                doc.document?.fileName ??
                                doc.fileName ??
                                doc.name ??
                                doc.key ??
                                `Document ${i + 1}`;
                              const url = doc.document?.url;
                              return url ? (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="fileChip"
                                >
                                  {label}
                                </a>
                              ) : (
                                <span key={i} className="fileChip">
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p>No documents attached.</p>
                        )}

                        <h3>Activity</h3>
                        <ul className="timeline">
                          {timeline.map((item) => (
                            <li key={item.id}>
                              <span className={`tlDot ${item.status}`.trim()}>
                                {item.status === "pending" ? "..." : "✓"}
                              </span>
                              <div>
                                <strong>{item.title}</strong>
                                <p>{item.meta}</p>
                              </div>
                            </li>
                          ))}
                        </ul>

                        {(co.status === S_APPROVED ||
                          co.status === S_REJECTED) && (
                          <div
                            className={`coDecisionBanner ${
                              co.status === S_APPROVED
                                ? "decisionApproved"
                                : "decisionRejected"
                            }`}
                          >
                            <span className="decisionIcon">
                              {co.status === S_APPROVED ? "✓" : "✕"}
                            </span>
                            <div className="decisionBody">
                              <strong>
                                {co.status === S_APPROVED
                                  ? "Approved by Owner"
                                  : "Rejected by Owner"}
                              </strong>
                              <p>
                                {co.status === S_APPROVED
                                  ? "This change order has been approved. The project contract value has been updated."
                                  : "This change order has been rejected. The GC has been notified and may revise and resubmit."}
                              </p>
                              {(co.lenderApprovedAt ?? co.lenderRejectedAt) && (
                                <span className="decisionDate">
                                  {formatDate(
                                    (co.status === S_APPROVED
                                      ? co.lenderApprovedAt
                                      : co.lenderRejectedAt) ?? "",
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </article>
              ) : null}
            </>
          )}
        </>
      )}

      <div className="infoCallout">
        <strong>CSC Role in Change Orders</strong>
        <p>
          CSC New England receives notifications on all change order submissions
          and decisions for record-keeping and FI benchmark tracking purposes.
          CSC does not approve or reject change orders — this authority rests
          with the project owner. Approved change orders automatically update
          the AIA G702 contract sum and the project's balance to finish in the
          platform.
        </p>
      </div>
    </section>
  );
}
